import appLogger from '@/utils/logger';
import { audienceDefaultValue } from '@/constants/audiences';
import { v4 as uuidv4 } from 'uuid';
import { AUDIENCE_CREATION_METHOD } from '@/constants/audiences';
import { IAudienceItem, IAudienceForm } from '@/types/audiences';
import {
  Briefcase,
  Building2,
  Calendar,
  ChromeIcon,
  Globe,
  Image as ImageIcon,
  MessageSquare,
  Mic,
  Radar,
  Search,
  Sparkles,
  Upload,
  Webhook,
} from 'lucide-react';
import React from 'react';

// Import ConditionGroup type directly to avoid circular dependency
import type { ConditionGroup } from '@/components/audiences/InlineConditionBuilder';

export const getFactIcon = (category: string) => {
  return `/icons/facts_icons/${category}.svg`;
};

/**
 * A "leaf group node" is a group's own conditions object -- {all|any: [...raw fact conditions]}
 * where every array element has a `.fact` property. A "fold node" is the same {all|any: [...]}
 * shape but produced by combining a prior group (or fold) with a new group via a per-pair
 * operator -- its first element is itself an all/any node, never a raw condition. Every real
 * fact condition object always has `.fact`, so checking the first array element's shape reliably
 * tells the two apart regardless of array length.
 */
const isLeafConditionsNode = (node: any): boolean => {
  if (!node || (!node.all && !node.any)) return true;
  const arr = node.all || node.any || [];
  if (arr.length === 0) return true;
  const first = arr[0];
  return !!first?.fact || (!first?.all && !first?.any);
};

/**
 * Walks a left-leaning fold tree (produced by useRuleBuilder's convertConditionsToRules: each
 * group after the first is combined with everything accumulated so far via `{[groupOperator]:
 * [accumulator, thisGroupNode]}`) and reconstructs the original left-to-right sequence of group
 * nodes with the operator that connects each one to its predecessor. The first group in the
 * returned list always has `operator: undefined` (nothing precedes it).
 */
export const flattenFoldedConditions = (
  node: any
): Array<{ operator: 'and' | 'or' | undefined; node: any }> => {
  if (isLeafConditionsNode(node)) {
    return [{ operator: undefined, node }];
  }

  const key = node.all ? 'all' : 'any';
  const thisOperator: 'and' | 'or' = key === 'all' ? 'and' : 'or';
  const [prior, latest] = node[key];

  const priorResults = isLeafConditionsNode(prior)
    ? [{ operator: undefined as 'and' | 'or' | undefined, node: prior }]
    : flattenFoldedConditions(prior);

  return [...priorResults, { operator: thisOperator, node: latest }];
};

// #4544: plain-language condition summary, one of the issue's own explicit UI requirements
// ("show a plain-language preview of the completed expression") that was never actually built
// for either Audience Builder or the Notification Policy list -- both currently show only raw
// fact/operator/value controls (in the edit form) or nothing at all (the policy list row, which
// shows only the audience name). Deliberately simple English verb phrases, not OPERATOR_LABEL's
// symbolic forms ("Equal (==)") -- this is for a non-technical admin glancing at a policy list,
// not someone actively editing a condition.
const CONDITION_OPERATOR_PHRASE: Record<string, string> = {
  equal: 'is', equals: 'is',
  notEqual: 'is not', notEquals: 'is not',
  notNull: 'is set',
  contains: 'contains',
  startsWith: 'starts with',
  endsWith: 'ends with',
  greaterThan: 'is more than',
  lessThan: 'is less than',
  greaterThanInclusive: 'is at least',
  lessThanInclusive: 'is at most',
  in: 'is one of',
  notIn: 'is not one of',
};

const factLabel = (fact: string): string => {
  if (!fact) return 'Unknown field';
  // camelCase -> Title Case, without pulling in the full IFact catalog (the policy list has no
  // reason to fetch it just to prettify a summary line): "customerInfoDomain" -> "Customer Info
  // Domain". Good enough for a glance-level summary; the full condition editor already shows
  // each fact's real description when someone drills into Audience Builder.
  return fact
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
};

export const conditionToPlainText = (condition: any): string => {
  if (!condition?.fact) return '';
  const phrase = CONDITION_OPERATOR_PHRASE[condition.operator] || condition.operator || '';
  if (condition.operator === 'notNull') return `${factLabel(condition.fact)} ${phrase}`;
  const value = Array.isArray(condition.value) ? condition.value.join(', ') : String(condition.value ?? '');
  return `${factLabel(condition.fact)} ${phrase} ${value}`.trim();
};

const groupNodeToPlainText = (node: any): string => {
  const key = node?.all ? 'all' : 'any';
  const items = (node?.[key] || []).map(conditionToPlainText).filter(Boolean);
  return items.join(key === 'all' ? ' and ' : ' or ');
};

/**
 * @param rules - The raw backend rules AST: an array whose single element's `conditions` is
 *   either a plain group node or a folded multi-group tree (see flattenFoldedConditions).
 * @returns A human-readable one-line summary, e.g. "Customer Info Domain is set and Page Views
 *   is 1" or, for multiple groups, "(Customer Info Domain is set and Page Views is 1) OR
 *   (Chatbot Session Started is set)".
 */
export const summarizeConditionsPlainText = (rules: unknown): string => {
  if (!Array.isArray(rules) || rules.length === 0) return 'No conditions configured';
  const first = rules[0] as { conditions?: any } | undefined;
  if (!first?.conditions) return 'No conditions configured';

  const flattened = flattenFoldedConditions(first.conditions);
  const multiGroup = flattened.length > 1;

  return flattened
    .map(({ operator, node }) => {
      const text = groupNodeToPlainText(node);
      const groupCount = (node?.all || node?.any || []).length;
      const wrapped = multiGroup && groupCount > 1 ? `(${text})` : text;
      return operator ? `${operator.toUpperCase()} ${wrapped}` : wrapped;
    })
    .filter(Boolean)
    .join(' ') || 'No conditions configured';
};

export const handleAudienceLoadRules = (rules: any): ConditionGroup[] => {
  try {
    // Return default for any falsy or empty rules
    if (!rules || (typeof rules === 'object' && Object.keys(rules).length === 0)) {
      // Convert audienceDefaultValue.conditionGroups to ConditionGroup[] format
      return audienceDefaultValue.conditionGroups.map((group) => ({
        id: uuidv4(),
        logicalOperator: (group.logicalOperator || 'and') as 'and' | 'or',
        groupOperator: (group.groupOperator || 'and') as 'and' | 'or',
        conditions: group.conditions.map((cond) => ({
          id: cond.id || uuidv4(),
          fact: cond.fact || '',
          operator: cond.operator || '',
          value: cond.value ?? null,
        })),
      }));
    }

    // Handle array format: rules is an array of rule groups
    // Format: [{ conditions: { all: [...] } }, { conditions: { any: [...] } }]
    if (Array.isArray(rules) && rules.length > 0) {
      // New format (produced by useRuleBuilder.convertConditionsToRules for 2+ groups):
      // a single array element whose conditions are a left-leaning fold tree encoding each
      // group's own per-pair operator. Must be checked before the legacy flat-array handling
      // below, which only ever reads the semantics of a single group correctly.
      if (rules.length === 1 && rules[0]?.conditions && !isLeafConditionsNode(rules[0].conditions)) {
        const flattened = flattenFoldedConditions(rules[0].conditions);
        const transformedFoldedGroups: ConditionGroup[] = flattened.map(({ operator, node: groupNode }) => {
          const groupOperatorKey = groupNode.all ? 'all' : 'any';
          const logicalOperator = groupOperatorKey === 'all' ? 'and' : 'or';
          const groupConditionsArray = groupNode[groupOperatorKey] || [];
          return {
            id: uuidv4(),
            logicalOperator: logicalOperator as 'and' | 'or',
            groupOperator: operator,
            conditions: groupConditionsArray.map((condition: any) => ({
              fact: condition.fact || '',
              operator: condition.operator || '',
              value: condition.value ?? null,
              id: uuidv4(),
            })),
          };
        });
        if (transformedFoldedGroups.length > 0) {
          return transformedFoldedGroups;
        }
      }

      const transformedConditionGroups: ConditionGroup[] = rules
        .map((ruleGroup: any): ConditionGroup | null => {
          const conditions = ruleGroup?.conditions;
          if (!conditions || typeof conditions !== 'object') {
            return null;
          }

          // Determine operator (all/any)
          const groupOperatorKey = conditions.all ? 'all' : 'any';
          const logicalOperator = groupOperatorKey === 'all' ? 'and' : 'or';
          const groupConditionsArray = conditions[groupOperatorKey];

          if (!groupConditionsArray || !Array.isArray(groupConditionsArray)) {
            return null;
          }

          // Map conditions to condition format with IDs
          const mappedConditions = groupConditionsArray.map((condition: any) => ({
            fact: condition.fact || '',
            operator: condition.operator || '',
            value: condition.value ?? null, // Preserve null, empty arrays, etc.
            id: uuidv4(),
          }));

          return {
            id: uuidv4(),
            logicalOperator: logicalOperator as 'and' | 'or',
            groupOperator: 'and' as 'and' | 'or', // Top-level operator between groups (default to 'and')
            conditions: mappedConditions,
          };
        })
        .filter((group): group is ConditionGroup => group !== null); // Type guard to remove null entries

      if (transformedConditionGroups.length > 0) {
        return transformedConditionGroups;
      }
      // Return default if no valid groups found
      return audienceDefaultValue.conditionGroups.map((group) => ({
        id: uuidv4(),
        logicalOperator: (group.logicalOperator || 'and') as 'and' | 'or',
        groupOperator: (group.groupOperator || 'and') as 'and' | 'or',
        conditions: group.conditions.map((cond) => ({
          id: cond.id || uuidv4(),
          fact: cond.fact || '',
          operator: cond.operator || '',
          value: cond.value ?? null,
        })),
      }));
    }

    // Handle single object format (legacy): { conditions: { all: [...] } }
    const conditions = rules?.conditions;
    if (!conditions || typeof conditions !== 'object') {
      return audienceDefaultValue.conditionGroups.map((group) => ({
        id: uuidv4(),
        logicalOperator: (group.logicalOperator || 'and') as 'and' | 'or',
        groupOperator: (group.groupOperator || 'and') as 'and' | 'or',
        conditions: group.conditions.map((cond) => ({
          id: cond.id || uuidv4(),
          fact: cond.fact || '',
          operator: cond.operator || '',
          value: cond.value ?? null,
        })),
      }));
    }

    const topLevelOperatorKey = conditions.all ? 'all' : 'any';
    const topLevelOperator = topLevelOperatorKey === 'all' ? 'and' : 'or';
    const groupConditionsArray = conditions[topLevelOperatorKey];

    if (!groupConditionsArray || !Array.isArray(groupConditionsArray)) {
      return audienceDefaultValue.conditionGroups.map((group) => ({
        id: uuidv4(),
        logicalOperator: (group.logicalOperator || 'and') as 'and' | 'or',
        groupOperator: (group.groupOperator || 'and') as 'and' | 'or',
        conditions: group.conditions.map((cond) => ({
          id: cond.id || uuidv4(),
          fact: cond.fact || '',
          operator: cond.operator || '',
          value: cond.value ?? null,
        })),
      }));
    }

    // Check if this is nested groups (each condition has all/any) or flat conditions
    if (groupConditionsArray?.[0]?.all || groupConditionsArray?.[0]?.any) {
      // Nested groups case
      const transformedConditionGroups: ConditionGroup[] = groupConditionsArray.map((groupCondition: any) => {
        const groupOperatorKey = groupCondition.all ? 'all' : 'any';
        const groupOperator = groupOperatorKey === 'all' ? 'and' : 'or';

        const nestedConditions = groupCondition[groupOperatorKey]?.map((condition: any) => ({
          fact: condition.fact || '',
          operator: condition.operator || '',
          value: condition.value ?? null,
          id: uuidv4(),
        }));

        return {
          id: uuidv4(),
          logicalOperator: groupOperator as 'and' | 'or',
          groupOperator: topLevelOperator as 'and' | 'or',
          conditions: nestedConditions || [],
        };
      });

      if (transformedConditionGroups.length > 0) {
        return transformedConditionGroups;
      }
      // Return default if no valid groups found
      return audienceDefaultValue.conditionGroups.map((group) => ({
        id: uuidv4(),
        logicalOperator: (group.logicalOperator || 'and') as 'and' | 'or',
        groupOperator: (group.groupOperator || 'and') as 'and' | 'or',
        conditions: group.conditions.map((cond) => ({
          id: cond.id || uuidv4(),
          fact: cond.fact || '',
          operator: cond.operator || '',
          value: cond.value ?? null,
        })),
      }));
    }

    // Flat conditions case (single group)
    return [
      {
        id: uuidv4(),
        logicalOperator: topLevelOperator as 'and' | 'or',
        groupOperator: 'and' as 'and' | 'or',
        conditions: groupConditionsArray.map((condition: any) => ({
          fact: condition.fact || '',
          operator: condition.operator || '',
          value: condition.value ?? null,
          id: uuidv4(),
        })),
      },
    ];
  } catch (error) {
    // Log error in development only
    if (process.env.NODE_ENV === 'development') {
      appLogger.error('Error parsing audience rules:', { args: [error, 'Rules:', rules] });
    }
    // Return default with proper type conversion
    return audienceDefaultValue.conditionGroups.map((group) => ({
      id: uuidv4(),
      logicalOperator: (group.logicalOperator || 'and') as 'and' | 'or',
      groupOperator: (group.groupOperator || 'and') as 'and' | 'or',
      conditions: group.conditions.map((cond) => ({
        id: cond.id || uuidv4(),
        fact: cond.fact || '',
        operator: cond.operator || '',
        value: cond.value ?? null,
      })),
    }));
  }
};

/**
 * Convert ConditionGroup[] to IAudienceForm.conditionGroups format
 * Handles type conversion from Condition.value (string | number | string[] | null) 
 * to IAudienceForm format (value?: string | undefined)
 */
const convertConditionGroupsToFormFormat = (
  conditionGroups: ConditionGroup[]
): IAudienceForm['conditionGroups'] => {
  return conditionGroups.map((group) => ({
    groupOperator: group.groupOperator,
    logicalOperator: group.logicalOperator,
    conditions: group.conditions.map((condition) => ({
      id: condition.id,
      fact: condition.fact,
      operator: condition.operator,
      // Convert value to string format expected by IAudienceForm
      value: condition.value === null || condition.value === undefined
        ? undefined
        : Array.isArray(condition.value)
          ? JSON.stringify(condition.value)
          : typeof condition.value === 'number'
            ? String(condition.value)
            : condition.value,
      conditionLogicalOperator: undefined, // Not used in current implementation
    })),
  }));
};

/**
 * Build conditions payload for backend API from IAudienceForm data
 * 
 * Transforms frontend conditionGroups format to backend API format:
 * - Frontend: { conditionGroups: [{ logicalOperator: 'and'|'or', groupOperator: 'and'|'or', conditions: [...] }] }
 * - Backend: { conditions: { all: [...] } } or { conditions: { all: [{ all: [...] }, { any: [...] }] } }
 * 
 * @param data - IAudienceForm data containing conditionGroups
 * @returns Backend API rules format: { conditions: { all|any: [...] } } or empty object if no groups
 */
export const buildConditions = (data: IAudienceForm): { conditions?: any } => {
  const groups = data.conditionGroups;

  if (!groups || groups.length === 0) {
    return {};
  }

  const groupConditions = groups.map((group) => {
    const conditions = group.conditions.map((condition) => ({
      fact: condition.fact,
      operator: condition.operator,
      value: condition.value,
    }));

    return {
      [group.logicalOperator === 'and' ? 'all' : 'any']: conditions,
    };
  });

  if (groupConditions.length === 1) {
    return {
      conditions: groupConditions[0],
    };
  }

  const topOperator = groups[0].groupOperator || 'and';

  return {
    conditions: {
      [topOperator === 'and' ? 'all' : 'any']: groupConditions,
    },
  };
};

const createDefaultRulesGroup = () => ({
  conditions: [{
    id: `rule-${Date.now()}`,
    field: '',
    operator: '',
    value: '',
  }],
  id: `group-${Date.now()}`,
  operator: 'and',
});

export const getAudienceTemplate = (
  method: AUDIENCE_CREATION_METHOD,
  overrides: Partial<IAudienceItem> = {},
): IAudienceItem => {
  const base: Partial<IAudienceItem> = {
    id: 0,
    name: '',
    type: 'person',
    permissions: 'user',
    creation_method: method,
  };

  switch (method) {
    case AUDIENCE_CREATION_METHOD.TIME_BASED:
      base.permissions = 'Anyone';
      base.schedule_config = {
        frequency: 'DAILY',
        days: [],
        dayOfWeek: [],
        times: ['15:30'],
        minutes: [],
        months: [],
      };
      base.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      break;
    case AUDIENCE_CREATION_METHOD.RULES:
      base.permissions = 'Anyone';
      base.rules = {
        conditions: [createDefaultRulesGroup()],
      };
      break;
    case AUDIENCE_CREATION_METHOD.HUBSPOT:
      base.permissions = 'user';
      base.type = 'person';
      break;
    case AUDIENCE_CREATION_METHOD.CSV:
      base.type = 'person';
      break;
    case AUDIENCE_CREATION_METHOD.WEBSITE_VISITS:
      base.type = 'person';
      base.permissions = 'user';
      // Website visit audiences start with no list_id (will be set after import)
      break;
    case AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS:
      base.type = 'person'; // Commenters are always people
      base.permissions = 'user';
      break;
    case AUDIENCE_CREATION_METHOD.SALESFORCE:
      base.type = 'company'; // Salesforce V1: company-level results only
      base.permissions = 'user';
      break;
    case AUDIENCE_CREATION_METHOD.AUDIENCE_BUILDER:
      base.type = 'person'; // AI search results are always people
      base.permissions = 'user';
      break;
    case AUDIENCE_CREATION_METHOD.FIBER_TAM:
      base.type = 'company'; // TAM Builder searches for companies
      base.permissions = 'user';
      break;
    case AUDIENCE_CREATION_METHOD.SIXSENSE:
      base.type = 'company'; // 6sense audiences are account-level only
      base.permissions = 'user';
      base.rules = [];
      break;
    case AUDIENCE_CREATION_METHOD.SCHEDULED_IMPORT:
      // Recurring CRM (SF/HS) import. Default to DAILY 09:00, empty rule config
      // — user fills source + filters in the creation form.
      base.type = 'person';
      base.permissions = 'user';
      base.schedule_config = {
        frequency: 'DAILY',
        days: [],
        dayOfWeek: [],
        times: ['09:00'],
        minutes: [],
        months: [],
      };
      base.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      base.rules = [];
      break;
    default:
      break;
  }

  return { ...(base as IAudienceItem), ...overrides };
};

export const buildAudiencePayload = (selectedAudience: IAudienceItem, data: any) => {
  const payload: any = {
    name: data.segmentName,
    type: selectedAudience.type,
    creation_method: selectedAudience.creation_method,
    permissions: data.permission || selectedAudience.permissions,
  };

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.TIME_BASED) {
    payload.start_time = data.startDate;
    payload.timezone = data.timezone;
    payload.schedule_config = data.scheduleConfig || {
      frequency: 'DAILY',
      times: ['15:30'],
      days: [],
      dayOfWeek: [],
      minutes: [],
      months: [],
    };
    // HubSpot CRM filter — only include fields explicitly provided to preserve patch semantics
    if (data.hubspot_field !== undefined) payload.hubspot_field = data.hubspot_field;
    if (data.hubspot_value !== undefined) payload.hubspot_value = data.hubspot_value;
    if (data.hubspot_operator !== undefined) payload.hubspot_operator = data.hubspot_operator;
    if (data.hubspot_object_type !== undefined) payload.hubspot_object_type = data.hubspot_object_type;
  }

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.RULES) {
    // Rules-based audiences: store rules for real-time matching
    // data.rules is the array of rule groups from convertConditionsToRules: [{ conditions: { all: [...] } }]
    // selectedAudience.rules is also an array when loaded from backend
    payload.rules = data.rules || selectedAudience.rules;
    payload.chat_token = null; // Rules-based audiences don't require chat_token
    // Rules-based audiences don't have list_id - they match in real-time
  }

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.HUBSPOT) {
    // For list mode: use list_id (DB list ID) - should be imported first
    // For field mode: use hubspot_field + hubspot_value + cached data
    // Backend logic: isListMode = !!list_id && !hubspot_field && !hubspot_value
    //                isSearchMode = !list_id && !!hubspot_field && ...
    // So we must NOT send list_id when in field search mode

    if (Array.isArray(data.filter_groups) && data.filter_groups.length > 0) {
      // Grouped field-search mode (same authored contract as Salesforce).
      payload.hubspot_object_type = data.hubspotObjectType || 'contacts';
      payload.hubspot_filter_groups = data.filter_groups;
    } else if (data.hubspotField) {
      // Field search mode: backend requires cached_contacts, cached_mappings, total_count
      // Backend will create a new list_id for field search mode
      // Do NOT send list_id - backend expects !list_id for search mode
      payload.hubspot_field = data.hubspotField;
      payload.hubspot_value = data.hubspotValue;
      payload.hubspot_operator = data.hubspotOperator;
      payload.cached_contacts = data.cachedContacts || [];
      payload.cached_mappings = data.cachedMappings || {};
      payload.total_count = data.totalCount || 0;
      payload.hubspot_object_type = data.hubspotObjectType || 'contacts';
      // Explicitly do NOT set list_id for field search mode
    } else if (data.listId) {
      // List mode: list_id is the DB list ID from import (for new audiences)
      payload.list_id = data.listId;
      // Send HubSpot list ID as top-level field for backend processing
      if (data.hubspotListId) {
        payload.hubspot_list_id = data.hubspotListId;
        // Also store in rules for reference when editing
        payload.rules = [{ hubspot_list_id: data.hubspotListId }];
      }
    } else if (data.hubspotListId) {
      // Editing list-based audience: send NEW HubSpot list ID (backend will create new list and import)
      // Do NOT send old list_id - backend will create new one
      payload.hubspot_list_id = data.hubspotListId;
      payload.rules = [{ hubspot_list_id: data.hubspotListId }];
    } else if (selectedAudience.list_id && !data.hubspotField) {
      // Editing non-HubSpot list-based audience: use existing list_id
      // Only set if NOT field search mode (no hubspotField) and NOT HubSpot list mode
      payload.list_id = selectedAudience.list_id;
    }
  }

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.CSV) {
    // For CSV audiences, list_id is critical - it links to the uploaded CSV data
    // When creating a new audience (id === 0), use data.listId from the CSV upload
    // When updating existing audience, keep using the existing list_id
    payload.list_id = data.listId || selectedAudience.list_id;
    // Note: start_time, batch_size, and batch_frequency are NOT set at creation.
    // These are configured at runtime via WorkflowExecutionModal when the workflow is executed.
    // The backend does not schedule CSV audiences at creation time - execution happens
    // when the user explicitly runs the workflow with their chosen batch processing parameters.
  }

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.WEBSITE_VISITS) {
    // For website visit audiences, list_id links to imported website visit data
    payload.list_id = selectedAudience.list_id;
    // Store rules (same format as rules-based audiences) so they can be loaded when editing
    // This provides consistency - both website visit and rules-based audiences use the rules column
    // Always include rules (even if empty array) to ensure consistency
    if (data.rules !== undefined) {
      payload.rules = data.rules;
    }
    // Note: start_time, batch_size, and batch_frequency are NOT set at creation.
    // These are configured at runtime via WorkflowExecutionModal when the workflow is executed.
    // Website visit audiences behave like CSV audiences for batch processing.
  }

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS) {
    // LinkedIn Post Commenters require linkedin_post_url AND list_id
    // list_id comes from /import-linkedin-commenters (called during Load in LinkedInCommentersAudienceForm)
    // data.listId is the freshly imported list; selectedAudience.list_id is the fallback for edits
    payload.linkedin_post_url = data.linkedinPostUrl;
    payload.list_id = data.listId || selectedAudience.list_id;
    payload.type = selectedAudience.type || 'person';
  }

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.FIBER_TAM) {
    payload.list_id = data.listId || selectedAudience.list_id;
  }

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.SALESFORCE) {
    // Backend expects salesforce_object and salesforce_filter_groups
    // SalesforceAudienceEditForm submits: { salesforce_object, filter_groups, segmentName, permission }
    payload.salesforce_object = data.salesforce_object;
    payload.salesforce_filter_groups = data.filter_groups;
    payload.type = 'company'; // V1: company-level only
  }

  if (selectedAudience.creation_method === AUDIENCE_CREATION_METHOD.SCHEDULED_IMPORT) {
    // Recurring CRM import. Form submits one of two shapes depending on source:
    //   Salesforce (SalesforceAudienceEditForm schedulable=true):
    //     { segmentName, permission, salesforce_object, filter_groups,
    //       scheduleConfig, timezone, relativeDateConfig: { enabled, field, daysAgo } }
    //   HubSpot (HubSpotAudienceEditForm schedulable=true):
    //     { segmentName, filter_groups, hubspotObjectType,
    //       scheduleConfig, timezone, ... }
    //
    // Source is determined from the audience template (set by openForm in
    // views/audiences/index.tsx based on the user's pick) — read from
    // selectedAudience.rules[0].source.
    const seededRules = Array.isArray(selectedAudience.rules)
      ? (selectedAudience.rules[0] as Record<string, unknown> | undefined)
      : (selectedAudience.rules as Record<string, unknown> | null | undefined);
    const source = (seededRules?.source as 'salesforce' | 'hubspot' | 'webhook' | undefined)
      ?? (data.runMode ? 'webhook' : (data.salesforce_object ? 'salesforce' : 'hubspot'));

    payload.start_time = new Date().toISOString();
    payload.type = 'person';

    // #3365: webhook source has no CRM query. run_mode drives the campaign cadence:
    // on_insert is event-driven (no schedule); on_schedule reuses schedule_config. Persist
    // run_mode in rules[0] and clear schedule_config when switching back to on_insert.
    if (source === 'webhook') {
      const runMode = (data.runMode as 'on_insert' | 'on_schedule' | undefined)
        ?? (seededRules?.run_mode as 'on_insert' | 'on_schedule' | undefined)
        ?? 'on_insert';
      payload.schedule_config = runMode === 'on_schedule' ? data.scheduleConfig : null;
      payload.timezone = runMode === 'on_schedule' ? data.timezone : null;
      payload.rules = [{
        source: 'webhook',
        run_mode: runMode,
        exclude_customers: data.excludeCustomers !== false,
      }];
      return payload;
    }

    payload.timezone = data.timezone;
    payload.schedule_config = data.scheduleConfig;
    // days_since_closed_lost is passed when the Salesforce form has the time-window
    // enabled — scheduledImportConsumer uses it to compute scheduled_at per contact
    // and to expand the filter from an exact day to an N-day range.
    if (data.days_since_closed_lost != null) {
      payload.days_since_closed_lost = data.days_since_closed_lost;
    }

    const ruleConfig: Record<string, unknown> = {
      source,
      // "Exclude existing customers" toggle (default ON). The import services read this off
      // rules[0] to gate filterOutExcludedCustomers; the send-time gate remains regardless.
      exclude_customers: data.excludeCustomers !== false,
    };
    if (source === 'salesforce') {
      ruleConfig.salesforce_object = data.salesforce_object;
      // Inject the relative-date condition back into filter_groups for the
      // backend resolveFilterGroups() to consume. The form keeps the toggle
      // state separate from filter_groups so the UX is clean.
      const baseFilterGroups = (Array.isArray(data.filter_groups) ? data.filter_groups : []) as Array<{
        group_operator: string;
        logical_operator: string;
        conditions: Array<Record<string, unknown>>;
      }>;
      const relativeDateConfig = data.relativeDateConfig as
        { enabled: boolean; field: string; mode?: 'single' | 'rolling'; days?: number; daysAgo?: number } | undefined;

      let finalFilterGroups: typeof baseFilterGroups = baseFilterGroups;
      if (relativeDateConfig?.enabled) {
        // Store a RANGE: >= (today - N) AND <= today. The closed-lost model imports
        // everyone in the window; per-contact outreach timing is owned by
        // people.scheduled_at = date + N (set at import). This is identical to what
        // the preview sends and what crmFilterBuilder.expandClosedLostWindow produces,
        // so stored filter == preview == import. The old exact-day ('equals') filter
        // resolved to a single calendar day and produced empty imports.
        const nDays = relativeDateConfig.days ?? relativeDateConfig.daysAgo ?? 120;
        const rangeConditions = [
          {
            id: `relative-date-start-${Date.now()}`,
            field: relativeDateConfig.field,
            operator: 'greater_than_or_equal',
            value: { relative_date: { offset: -Math.abs(nDays), unit: 'days' } },
          },
          {
            id: `relative-date-end-${Date.now()}`,
            field: relativeDateConfig.field,
            operator: 'less_than_or_equal',
            value: { relative_date: { offset: 0, unit: 'days' } },
          },
        ];
        if (baseFilterGroups.length === 0) {
          finalFilterGroups = [{
            group_operator: 'and',
            logical_operator: 'and',
            conditions: rangeConditions,
          }];
        } else {
          finalFilterGroups = baseFilterGroups.map((g, idx) => (idx === 0
            ? { ...g, conditions: [...g.conditions.filter((c) => c.field && c.operator), ...rangeConditions] }
            : g));
        }
      }
      ruleConfig.filter_groups = finalFilterGroups;
    } else if (source === 'hubspot') {
      ruleConfig.hubspot_object_type = data.hubspotObjectType || 'contacts';
      ruleConfig.filter_groups = data.filter_groups;
      ruleConfig.field_mappings = data.cachedMappings;
    }
    payload.rules = [ruleConfig];
  }

  return payload;
};

/**
 * Format creation method for display
 * Converts internal creation method values to user-friendly labels
 */
export const formatCreationMethod = (method: string): string => {
  switch (method) {
    case 'csv':
      return 'CSV upload';
    case 'website_visits':
      return 'Website Visit';
    case 'hubspot':
      return 'Hubspot';
    case 'rules':
      return 'Rules based';
    case 'scheduled':
      return 'Time based';
    case 'chrome_extension':
      return 'Chrome extension';
    case 'voice_recordings':
      return 'Voice';
    case 'gtm_signal':
      return 'GTM Signal';
    case 'linkedin_post_commenters':
      return 'LinkedIn Post Commenters';
    case 'conference_scout':
      return 'Conference Scout';
    case 'pre_meeting_brief':
      return 'Pre-Meeting Brief';
    case 'audience_builder':
      return 'AI Search';
    case 'fiber_tam':
      return 'TAM Builder';
    case 'sixsense_accounts':
      return '6sense Accounts';
    case 'scheduled_import':
      return 'Scheduled Import';
    case 'webhook': // #3365 FE label for scheduled_import audiences with rules[0].source='webhook'
      return 'Webhook';
    case 'loop': // a rep's nurture loop audience (mb-frontend#4458)
      return 'Loop';
    default:
      return method;
  }
};

/**
 * Get badge color classes for creation method
 * Returns Tailwind classes for creation method badges
 */
export const getCreationMethodColor = (method: string): string => {
  switch (method) {
    case 'csv':
      return 'bg-cyan-50/70 text-cyan-500';
    case 'website_visits':
      return 'bg-indigo-50/70 text-indigo-400';
    case 'hubspot':
      return 'bg-orange-50/70 text-orange-400';
    case 'rules':
      return 'bg-violet-50/70 text-violet-400';
    case 'scheduled':
      return 'bg-teal-50/70 text-teal-500';
    case 'chrome_extension':
      return 'bg-amber-50/70 text-amber-500';
    case 'voice_recordings':
      return 'bg-rose-50/70 text-rose-400';
    case 'gtm_signal':
      return 'bg-emerald-50/70 text-emerald-500';
    case 'linkedin_post_commenters':
      return 'bg-blue-50/70 text-blue-400';
    case 'conference_scout':
      return 'bg-cyan-50/70 text-cyan-500';
    case 'pre_meeting_brief':
      return 'bg-purple-50/70 text-purple-400';
    case 'audience_builder':
      return 'bg-indigo-50/70 text-indigo-500';
    case 'fiber_tam':
      return 'bg-emerald-50/70 text-emerald-500';
    case 'sixsense_accounts':
      return 'bg-mb-blue-50 text-mb-blue-700';
    case 'scheduled_import':
      return 'bg-teal-50/70 text-teal-500';
    case 'webhook':
      return 'bg-violet-50/70 text-violet-500';
    case 'loop':
      return 'bg-indigo-50/70 text-indigo-600';
    default:
      return 'bg-slate-50/70 text-slate-400';
  }
};

/**
 * #3365 — Webhook audiences are persisted as creation_method='scheduled_import' with
 * rules[0].source='webhook'. There is no creation_method='webhook' row; 'webhook' is a
 * virtual display type. These helpers are the single source of truth for detecting and
 * labeling webhook audiences consistently across every surface (audience detail, the
 * audiences list, and the campaigns list) so the type/label/run-mode never drift.
 */
type AudienceTypeShape = { creation_method?: string; rules?: any } | null | undefined;

export type CampaignDisplayStatus = NonNullable<IAudienceItem['derived_status']> | 'paused';

/**
 * Resolve the user-facing campaign status from execution state plus the publish gate.
 * `derived_status` intentionally describes execution history/pending work, so a paused
 * campaign can remain `in_progress`. A confirmed `published_at: null` must win for display.
 * Treat an omitted `published_at` as unknown rather than paused for reduced API records.
 */
export const getCampaignDisplayStatus = (
  audience: Pick<IAudienceItem, 'derived_status' | 'published_at'> | null | undefined,
): CampaignDisplayStatus => {
  const status = audience?.derived_status || 'new';
  if (status === 'in_progress' && audience?.published_at === null) return 'paused';
  return status;
};

/**
 * The confirmation for a run that was scheduled rather than started, or null when the
 * run started now. When `scheduled_for` is set there is no run to watch yet — a list
 * campaign's first batch is dispatched at that time, not on click.
 */
export const getScheduledWorkflowStartMessage = (
  response: { message?: string; scheduled_for?: string } | null | undefined,
): string | null => {
  if (!response?.scheduled_for) return null;
  const start = new Date(response.scheduled_for);
  if (Number.isNaN(start.getTime())) return response.message || 'Workflow scheduled.';
  return `Scheduled to start ${start.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })}`;
};

/** True only while the campaign execution is in progress and its publish gate is open. */
export const isCampaignRunning = (
  audience: Pick<IAudienceItem, 'derived_status' | 'published_at'> | null | undefined,
): boolean => getCampaignDisplayStatus(audience) === 'in_progress';

type CampaignProgressShape = Pick<
  IAudienceItem,
  'derived_status' | 'execution_stats' | 'email_metrics' | 'people_count' | 'total_count'
> | null | undefined;

/**
 * Audience size, resolved the same way on every surface. `execution_stats.total_people`
 * is the list endpoint's own copy of `people_count`; keeping one precedence order stops
 * two cards quoting different denominators for the same campaign.
 */
export const getCampaignPeopleTotal = (audience: CampaignProgressShape): number => (
  audience?.execution_stats?.total_people ?? audience?.people_count ?? audience?.total_count ?? 0
);

export interface CampaignProgress {
  /** False when the campaign has no audience yet — render "Not started yet". */
  hasProgress: boolean;
  totalPeople: number;
  /** Messages actually delivered, capped at the audience size. */
  sent: number;
  /** `sent / totalPeople` as a whole percentage, 0 when there is no audience. */
  percent: number;
  /** Recipients the campaign failed to send to, capped at the audience size. */
  failed: number;
  isComplete: boolean;
}

/**
 * The one definition of campaign progress. Every surface that renders "X of Y" for a
 * campaign must use this — /campaigns and the SDR Today dashboard disagreed on 93 of 204
 * live campaigns (46%) before this existed, because they picked different numerators.
 *
 * The numerator is **messages sent**, not `processed`. `processed` counts audience rows
 * the pipeline has queued (`people.processed_at`), so it reaches the total long before
 * the workers have sent anything — which is how Sara's construction campaign came to read
 * "33 of 33" beside a Sent column saying 19. Sent is cumulative across touches, so it is
 * capped at the audience size or a multi-touch sequence would exceed 100%.
 *
 * `isComplete` deliberately reads raw `derived_status`, not `getCampaignDisplayStatus`:
 * a paused campaign stays `in_progress` underneath, and treating it as complete would
 * paint the bar green and label it "Completed" while its publish gate is shut (#4213).
 */
export const getCampaignProgress = (audience: CampaignProgressShape): CampaignProgress => {
  const totalPeople = getCampaignPeopleTotal(audience);
  const sent = Math.min(audience?.email_metrics?.emails_sent ?? 0, totalPeople);
  return {
    hasProgress: totalPeople > 0,
    totalPeople,
    sent,
    percent: totalPeople > 0 ? Math.min(100, Math.round((sent / totalPeople) * 100)) : 0,
    failed: Math.min(audience?.execution_stats?.error ?? 0, totalPeople),
    isComplete: totalPeople > 0 && sent >= totalPeople && audience?.derived_status !== 'in_progress',
  };
};

type CampaignRowShape = Pick<
  IAudienceItem,
  'derived_status' | 'published_at' | 'execution_stats' | 'email_metrics' | 'people_count'
  | 'total_count' | 'next_batch_process_time' | 'creation_method' | 'rules'
> | null | undefined;

/** Statuses whose run has ended: nothing is queued and the history is final. */
const FINISHED_CAMPAIGN_STATUSES: ReadonlySet<CampaignDisplayStatus> = new Set<CampaignDisplayStatus>([
  'completed', 'replied', 'partial', 'error', 'stalled',
]);

export interface CampaignRowState {
  /** The one status for the row — the pill, the progress subline and the filter chip all read it. */
  status: CampaignDisplayStatus;
  /** Pill text, `formatWorkflowStatus(status)`. */
  label: string;
  /** Text beside the "sent/recipients" bar. Always consistent with `status`. */
  progressLabel: string;
  /**
   * Failed workflow STEPS (`execution_stats.error` counts play executions, not people).
   * It is never presented as "N of M recipients didn't send": a one-recipient campaign
   * with 13 failed step attempts and 1 sent email read "1 of 1 didn't send" beside Sent=1.
   */
  failedSteps: number;
  /** The run has ended (completed, replied, partial, needs attention, stalled). */
  isFinished: boolean;
}

/**
 * The single derivation of a /campaigns row. Before this, the pill read `derived_status`
 * while the progress subline re-derived its own state from `next_batch_process_time` and
 * the sent count, so rows said "Completed" beside "0/1 Not scheduled" and "Ready" beside
 * "Sending 0/24" (an unpublished campaign whose last batch time had passed).
 */
export const getCampaignRowState = (audience: CampaignRowShape): CampaignRowState => {
  const status = getCampaignDisplayStatus(audience);
  const { hasProgress } = getCampaignProgress(audience);
  const isOnInsertWebhook = isWebhookAudience(audience) && getAudienceWebhookRunMode(audience) === 'on_insert';

  let progressLabel: string;
  switch (status) {
    case 'paused':
      progressLabel = 'Paused';
      break;
    case 'scheduled':
      progressLabel = 'Scheduled';
      break;
    case 'completed':
    case 'replied':
      progressLabel = 'Finished';
      break;
    case 'partial':
      progressLabel = 'Finished with errors';
      break;
    case 'error':
      progressLabel = 'Failed';
      break;
    case 'stalled':
      progressLabel = 'Stalled';
      break;
    case 'in_progress': {
      const next = audience?.next_batch_process_time ? new Date(audience.next_batch_process_time) : null;
      if (isOnInsertWebhook) {
        progressLabel = next && next <= new Date() && hasProgress ? 'Sending' : 'Auto on insert';
      } else if (next && next > new Date()) {
        progressLabel = `Next: ${next.toLocaleString('en-US', {
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        })}`;
      } else {
        const stats = audience?.execution_stats;
        const inDelay = stats?.sub_status === 'in_delay' || (!stats?.sub_status && (stats?.pending_next ?? 0) > 0);
        progressLabel = inDelay ? 'In sequence' : 'Sending';
      }
      break;
    }
    default:
      // ready / new: never started (or stopped and unpublished). Whatever
      // next_batch_process_time says, nothing is sending.
      progressLabel = 'Not started';
  }

  return {
    status,
    label: formatWorkflowStatus(status),
    progressLabel,
    failedSteps: Math.max(0, audience?.execution_stats?.error ?? 0),
    isFinished: FINISHED_CAMPAIGN_STATUSES.has(status),
  };
};

export const isWebhookAudience = (audience: AudienceTypeShape): boolean => (
  audience?.creation_method === 'scheduled_import'
  && (audience?.rules as any)?.[0]?.source === 'webhook'
);

/** Run mode for a webhook audience: 'on_insert' (event-driven) or 'on_schedule' (cron). */
export const getAudienceWebhookRunMode = (
  audience: AudienceTypeShape,
): 'on_insert' | 'on_schedule' | undefined => (audience?.rules as any)?.[0]?.run_mode;

/**
 * Resolve the display creation-method key for an audience, mapping webhook-source
 * scheduled_import audiences to the virtual 'webhook' type. Feed the result into
 * formatCreationMethod / getCreationMethodColor / getCreationMethodIcon.
 */
export const getAudienceTypeKey = (audience: AudienceTypeShape): string => (
  isWebhookAudience(audience) ? 'webhook' : (audience?.creation_method || '')
);

/** Human-readable type label for an audience, webhook-aware. */
export const getAudienceTypeLabel = (audience: AudienceTypeShape): string => (
  formatCreationMethod(getAudienceTypeKey(audience))
);

/**
 * Format derived workflow status for display
 */
export const formatWorkflowStatus = (status: string): string => {
  switch (status) {
    case 'new':
      return 'New';
    case 'ready':
      return 'Ready';
    case 'scheduled':
      return 'Scheduled';
    case 'in_progress':
      return 'Running';
    case 'paused':
      return 'Paused';
    case 'completed':
      return 'Completed';
    case 'replied':
      return 'Replied';
    case 'partial':
      return 'Partial';
    case 'error':
      return 'Needs attention';
    case 'stalled':
      return 'Stalled';
    default:
      return status;
  }
};

/**
 * Badge style classes for an audience's derived workflow status.
 * Single source of truth shared by the campaigns hub table and the notification drawer so the
 * same campaign status renders identically across surfaces. Pair with `formatWorkflowStatus`.
 */
export const WORKFLOW_STATUS_BADGE_STYLE: Record<string, string> = {
  new: 'bg-slate-100 text-slate-600 border-slate-200/60 shadow-[0_0_5px_rgba(0,0,0,0.12)]',
  ready: 'bg-amber-100 text-amber-700 border-amber-200/60 shadow-[0_0_5px_rgba(217,119,6,0.18)]',
  scheduled: 'bg-indigo-100 text-indigo-700 border-indigo-200/60 shadow-[0_0_5px_rgba(79,70,229,0.18)]',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200/60 shadow-[0_0_5px_rgba(37,99,235,0.18)]',
  paused: 'bg-slate-100 text-slate-700 border-slate-300/70 shadow-[0_0_5px_rgba(71,85,105,0.14)]',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200/60 shadow-[0_0_5px_rgba(5,150,105,0.18)]',
  partial: 'bg-orange-100 text-orange-700 border-orange-200/60 shadow-[0_0_5px_rgba(234,88,12,0.18)]',
  error: 'bg-red-100 text-red-700 border-red-200/60 shadow-[0_0_5px_rgba(220,38,38,0.18)]',
  replied: 'bg-emerald-100 text-emerald-700 border-emerald-200/60 shadow-[0_0_5px_rgba(5,150,105,0.18)]',
  stalled: 'bg-rose-100 text-rose-700 border-rose-200/60 shadow-[0_0_5px_rgba(225,29,72,0.18)]',
};

/**
 * Get badge color classes for workflow execution status
 */
export const getWorkflowStatusColor = (status: string): string => {
  switch (status) {
    case 'new':
      return 'bg-slate-50 text-slate-400';
    case 'ready':
      return 'bg-slate-50/80 text-slate-500';
    case 'in_progress':
      return 'bg-primary-50/80 text-primary-400';
    case 'paused':
      return 'bg-slate-50 text-slate-500';
    case 'completed':
      return 'bg-emerald-50/70 text-emerald-500';
    case 'replied':
      return 'bg-violet-50/70 text-violet-400';
    case 'error':
      return 'bg-red-50/60 text-red-400';
    case 'stalled':
      return 'bg-rose-50/70 text-rose-500';
    default:
      return 'bg-slate-50 text-slate-400';
  }
};

/**
 * Normalize permission value to standard format
 * Maps legacy permission values ('Anyone', 'Only admins') to standard values ('user', 'admin')
 */
export const normalizePermission = (permission: string): 'user' | 'admin' => {
  if (permission === 'Anyone') return 'user';
  if (permission === 'Only admins') return 'admin';
  return (permission as 'user' | 'admin') || 'user';
};

/**
 * Get submit button text based on audience creation method
 * 
 * "Import" - Methods that bring external data into the system:
 * - CSV: Import from file upload
 * - Images: Import from image processing
 * - Voice: Import from voice recordings
 * - HubSpot: Import from HubSpot integration
 * 
 * "Save" - Methods that configure internal audiences:
 * - Rules: Save rule-based audience configuration
 * - Time-based: Save scheduled audience settings
 * - Chrome Extension: Save extension-captured audience
 * 
 * @param creationMethod - The audience creation method
 * @returns Button text: 'Import' or 'Save'
 */
export const getSubmitButtonText = (creationMethod: string): 'Import' | 'Save' => {
  const IMPORT_METHODS = [
    AUDIENCE_CREATION_METHOD.CSV,
    AUDIENCE_CREATION_METHOD.IMAGES,
    AUDIENCE_CREATION_METHOD.HUBSPOT,
  ];

  return IMPORT_METHODS.includes(creationMethod as AUDIENCE_CREATION_METHOD) ? 'Import' : 'Save';
};

/**
 * Get icon component for audience creation method
 * Returns a React element with the appropriate icon and color for the creation method
 * 
 * @param creationMethod - The audience creation method
 * @returns React element with icon component, or null if method not found
 */
export const getCreationMethodIcon = (
  creationMethod: string,
): React.ReactElement | null => {
  const iconMap: Record<string, React.ReactElement> = {
    [AUDIENCE_CREATION_METHOD.CSV]: <Upload size={16} color='#0066ff' />,
    [AUDIENCE_CREATION_METHOD.RULES]: <Globe size={16} color='#0066ff' />,
    [AUDIENCE_CREATION_METHOD.IMAGES]: <ImageIcon size={16} color='#0066ff' />,
    [AUDIENCE_CREATION_METHOD.VOICE]: <Mic size={16} color='#0066ff' />,
    [AUDIENCE_CREATION_METHOD.TIME_BASED]: <Calendar size={16} color='#0066ff' />,
    [AUDIENCE_CREATION_METHOD.CHROME_EXTENSION]: <ChromeIcon size={16} color='#0066ff' />,
    [AUDIENCE_CREATION_METHOD.HUBSPOT]: <Building2 size={16} color='#ff7a59' />,
    [AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS]: <MessageSquare size={16} color='#0077b5' />,
    [AUDIENCE_CREATION_METHOD.CONFERENCE_SCOUT]: <Search size={16} color='#10b981' />,
    [AUDIENCE_CREATION_METHOD.PRE_MEETING_BRIEF]: <Briefcase size={16} color='#8b5cf6' />,
    [AUDIENCE_CREATION_METHOD.AUDIENCE_BUILDER]: <Sparkles size={16} color='#6366f1' />,
    [AUDIENCE_CREATION_METHOD.FIBER_TAM]: <Building2 size={16} color='#10b981' />,
    [AUDIENCE_CREATION_METHOD.SIXSENSE]: <Radar size={16} color='#0066ff' />,
    // #3365: virtual webhook type (scheduled_import + rules[0].source='webhook'), resolved
    // via getAudienceTypeKey. scheduled_import (CRM) reuses the calendar icon.
    webhook: <Webhook size={16} color='#8b5cf6' />,
    scheduled_import: <Calendar size={16} color='#0066ff' />,
  };

  return iconMap[creationMethod] || null;
};
